import { RoutingTabs } from '../components/routing/useRoutingTabs';
import { PAGE_VISIT_EVENTS } from '../services/events';
import documentationImg from '../components/dashboard/fetching-env-page/images/documentation.png';
import { STORYLANE_URLS } from '../components/common/url-consts';
import type { PageblockData } from '../constants/pageblockTypes';
import {
  PAGEBLOCK_DATA_DOCUMENTATION,
  PAGEBLOCK_DATA_AUTOMATIONS,
  PAGEBLOCK_DATA_ALERTS,
  PAGEBLOCK_DATA_ROUTING,
  PAGEBLOCK_DATA_AGENTIC_LAYER,
  PAGEBLOCK_DATA_AI_ASSISTANT,
  PAGEBLOCK_DATA_DEDUP,
  PAGEBLOCK_DATA_DEVOPS_CENTER,
  PAGEBLOCK_DATA_FUNNEL_MAPS,
  PAGEBLOCK_DATA_MARKETING,
  PAGEBLOCK_DATA_REPORTS,
  PAGEBLOCK_DATA_ROLLUPS,
  PAGEBLOCK_DATA_SF_CHANGE_FEED,
} from '../constants/pageblockData';
import { OrgType } from '../apis/facades/types/crmOrgsApiFacade.types';

export const ROUTING_TAB = 'tab';

export type AppRoute = {
  route: string;
  isRequiredDefaultCreationEnvConnection: boolean;
  // Define additional subroutes (beyond the base route) that need env redirection
  subroutesForRedirectToEnv?: string[];
  skipRedirectCallbackAuth0: boolean;
  disableRedirectToOnboarding: boolean;
  isPaywallBlock?: boolean; //true for pages that are fully blocked by paywall
  documentTitle?: string; //if not provided, a default will be used as document title
  mixpanelPageVisitEvent?: PAGE_VISIT_EVENTS;
  initialFetchBlock?: {
    //add only in case we need a page block during initial fetch
    imageSrc: string;
    url: string;
    title: string;
  };
  pageblockData?: PageblockData;
  /** When set, the route is only available for these org types. Undefined = no restriction. */
  restrictToOrgTypes?: OrgType[];
};

export const appRoutes: { [key: string]: AppRoute } = {
  orgMigration: {
    route: '/org-migration',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    documentTitle: 'Org Migration - Sweep',
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  documentation: {
    route: '/documentation',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    documentTitle: 'Documentation - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.documentation,
    initialFetchBlock: {
      imageSrc: documentationImg,
      url: STORYLANE_URLS.documentation,
      title: 'Documentation',
    },
    pageblockData: PAGEBLOCK_DATA_DOCUMENTATION,
  },
  automations: {
    route: '/automations',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    isPaywallBlock: true,
    documentTitle: 'Automations - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.automations,
    pageblockData: PAGEBLOCK_DATA_AUTOMATIONS,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  alerts: {
    route: '/alerts',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    isPaywallBlock: true,
    documentTitle: 'Alerts - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.alerts,
    pageblockData: PAGEBLOCK_DATA_ALERTS,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  routing: {
    route: '/routing',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    isPaywallBlock: true,
    documentTitle: 'Routing - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.routing,
    pageblockData: PAGEBLOCK_DATA_ROUTING,
    subroutesForRedirectToEnv: [`${ROUTING_TAB}/:routingTab`],
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  marketingManagement: {
    route: '/marketing-management',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    isPaywallBlock: true,
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.marketingManagement,
    pageblockData: PAGEBLOCK_DATA_MARKETING,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  dedup: {
    route: '/dedup',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    isPaywallBlock: true,
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.dedup,
    documentTitle: 'Dedupe and Matching - Sweep',
    pageblockData: PAGEBLOCK_DATA_DEDUP,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  getStarted: {
    route: '/get-started',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    documentTitle: 'Get Started - Sweep',
  },
  settings: {
    route: '/settings',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    documentTitle: 'Settings - Sweep',
  },
  funnelMapsPage: {
    route: '/funnels',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    documentTitle: 'Funnel Editor - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.funnels,
    pageblockData: PAGEBLOCK_DATA_FUNNEL_MAPS,
    restrictToOrgTypes: [OrgType.Salesforce, OrgType.Snowflake],
  },
  sfChangeFeed: {
    route: '/change-feed',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    documentTitle: 'Change Feed - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.changeFeed,
    pageblockData: PAGEBLOCK_DATA_SF_CHANGE_FEED,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  rollups: {
    route: '/rollups',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    documentTitle: 'Rollups - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.rollups,
    pageblockData: PAGEBLOCK_DATA_ROLLUPS,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  reports: {
    route: '/reports',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    isPaywallBlock: true,
    documentTitle: 'Reports - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.reports,
    pageblockData: PAGEBLOCK_DATA_REPORTS,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  devopsCenter: {
    route: '/compare-and-deploy',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    isPaywallBlock: true,
    documentTitle: 'Compare and Deploy - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.devopsCenter,
    pageblockData: PAGEBLOCK_DATA_DEVOPS_CENTER,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  aiAssistant: {
    route: '/support-ai',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    isPaywallBlock: true,
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.aiAssistant,
    documentTitle: 'Support AI - Sweep',
    pageblockData: PAGEBLOCK_DATA_AI_ASSISTANT,
    restrictToOrgTypes: [OrgType.Salesforce],
  },
  agenticLayer: {
    route: '/agentic',
    subroutesForRedirectToEnv: [`agent/:agentId`, `agent/:agentId/chat/:chatId`],
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    documentTitle: 'Agentic Layer - Sweep',
    isPaywallBlock: true,
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.agentic,
    pageblockData: PAGEBLOCK_DATA_AGENTIC_LAYER,
  },
  projects: {
    route: '/projects',
    subroutesForRedirectToEnv: ['agent/:agentId', 'agent/:agentId/chat/:chatId'],
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: true,
    isPaywallBlock: true,
    documentTitle: 'Projects - Sweep',
    mixpanelPageVisitEvent: PAGE_VISIT_EVENTS.projects,
    pageblockData: PAGEBLOCK_DATA_AGENTIC_LAYER,
  },
  external: {
    route: '/external',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  salesforce: {
    route: '/salesforce-oauth2-redirect',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: true,
    isRequiredDefaultCreationEnvConnection: false,
  },
  hubspotRedirect: {
    route: '/hubspot-oauth2-redirect',
    skipRedirectCallbackAuth0: true,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  teamsRedirect: {
    route: '/teams-oauth2-redirect',
    skipRedirectCallbackAuth0: true,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  mcpRedirect: {
    route: '/mcp-oauth-callback',
    skipRedirectCallbackAuth0: true,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  demoComponents: {
    route: '/demo-components',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  demoRuleBuilder: {
    route: '/demo-rule-builder',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  home: {
    route: '/',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  slack: {
    route: '/slack/oauth2-redirect',
    skipRedirectCallbackAuth0: true,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  version: {
    route: '/version',
    skipRedirectCallbackAuth0: true,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  freeTierOnBoarding: {
    route: '/onboarding',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  funnelEditor: {
    route: '/multi-funnel/flow',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    documentTitle: 'Funnel Editor - Sweep',
    restrictToOrgTypes: [OrgType.Salesforce, OrgType.Snowflake],
  },
  funnelEditorReadOnly: {
    route: '/multi-funnel/view',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    restrictToOrgTypes: [OrgType.Salesforce, OrgType.Snowflake],
  },
  canvasPreviewTest: {
    route: '/preview-test',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    documentTitle: 'Canvas Preview Test - Sweep',
  },
  questionnaire: {
    route: '/questionnaire',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
  shareArtifacts: {
    route: '/share-artifacts',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
    documentTitle: 'Shared Artifact - Sweep',
  },
  createAccount: {
    route: '/create-account',
    skipRedirectCallbackAuth0: false,
    disableRedirectToOnboarding: false,
    isRequiredDefaultCreationEnvConnection: false,
  },
};

export const ROUTING_GROUPS = `${appRoutes.routing.route}/${ROUTING_TAB}/${RoutingTabs.groups}`;
export const ROUTING_TERRITORIES = `${appRoutes.routing.route}/${ROUTING_TAB}/${RoutingTabs.territories}`;
export const ROUTING_ASSIGNMENT_RULES = `${appRoutes.routing.route}/${ROUTING_TAB}/${RoutingTabs.assignment}`;

const allAppRoutes = Object.values(appRoutes);
const getRoutesByCriteria = (filterFn: (route: AppRoute) => boolean) =>
  getAppRoutesByCriteria(filterFn).map((r) => r.route);

const getAppRoutesByCriteria = (filterFn: (route: AppRoute) => boolean) =>
  allAppRoutes.filter(filterFn);

//returns the first item that matches this path
export const getAppRouteByPath = (path: string) => {
  return allAppRoutes.find((appRoute) => path.startsWith(appRoute.route));
};

export const RoutesToSkipRedirectCallbackAuth0 = getRoutesByCriteria(
  (route) => route.skipRedirectCallbackAuth0,
);
export const RoutesToDisableRedirectToOnboarding = getRoutesByCriteria(
  (route) => route.disableRedirectToOnboarding,
);
export const RoutesThatRequireDefaultCreationEnvConnection = getRoutesByCriteria(
  (route) => route.isRequiredDefaultCreationEnvConnection,
);
export const RoutesThatRequireInitialFetch = getRoutesByCriteria(
  (route) => !!route.initialFetchBlock,
);
export const PaywallRoutes = getRoutesByCriteria((route) => !!route.isPaywallBlock);
export const AppRoutesWithTitles = getAppRoutesByCriteria((route) => !!route.documentTitle);
export const AppRoutesWithPageVisitEvent = getAppRoutesByCriteria(
  (route) => !!route.mixpanelPageVisitEvent,
);

export const isRouteEnabledForOrgType = (
  appRoute: AppRoute,
  orgType: OrgType | undefined,
): boolean => {
  if (!appRoute.restrictToOrgTypes || !orgType) return true;
  return appRoute.restrictToOrgTypes.includes(orgType);
};

const appRoutesByPath = new Map(allAppRoutes.map((r) => [r.route, r]));

export const isRoutePathEnabledForOrgType = (
  routePath: string,
  orgType: OrgType | undefined,
): boolean => {
  const appRoute = appRoutesByPath.get(routePath);
  if (!appRoute) return true;
  return isRouteEnabledForOrgType(appRoute, orgType);
};

/** Finds the best-matching AppRoute for a full pathname (longest prefix wins, ignores home `/`). */
export const findAppRouteByPathname = (pathname: string): AppRoute | undefined => {
  let bestMatch: AppRoute | undefined;
  for (const appRoute of allAppRoutes) {
    if (appRoute.route === '/') continue;
    if (
      pathname.startsWith(appRoute.route) &&
      (!bestMatch || appRoute.route.length > bestMatch.route.length)
    ) {
      bestMatch = appRoute;
    }
  }
  return bestMatch;
};

export const DEFAULT_ROUTE = appRoutes.getStarted.route;
